import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Award, ExternalLink, Radio, ChevronDown, Newspaper, Maximize2, X, CheckCircle2 } from 'lucide-react';
import { RECOGNITION, RADIO_FEATURE, PRESS_ITEMS } from '../mock';
import { Reveal } from './Shared';
import { useLang } from '../context/LangContext';

export const AwardCard = () => {
  const { tr } = useLang();
  return (
  <a
    data-testid="threebestrated-award-card"
    href={RECOGNITION.url}
    target="_blank"
    rel="noreferrer"
    className="group card-lift relative overflow-hidden rounded-3xl p-7 md:p-8 text-white flex flex-col justify-between h-full"
    style={{ background: 'linear-gradient(140deg,#0b3b3a 0%,#0f766e 100%)' }}
  >
    <span className="absolute -right-10 -top-10 w-44 h-44 rounded-full" style={{ background: 'rgba(255,122,99,0.18)' }} />
    <div className="relative">
      <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-teal-100">
        <Award size={16} /> {tr('Recognised by', 'सम्मान:')} {RECOGNITION.source}
      </span>
      <div className="flex items-end gap-4 mt-5">
        <span className="font-display text-6xl leading-none" style={{ fontWeight: 600, color: '#ffb3a6' }}>Top 3</span>
      </div>
      <h3 className="text-xl md:text-2xl mt-3 text-balance" style={{ fontWeight: 600 }}>{tr(RECOGNITION.title, RECOGNITION.hiTitle)}</h3>
      <p className="text-teal-100/90 text-sm mt-3 leading-relaxed">{tr(RECOGNITION.body, RECOGNITION.hiBody)}</p>
    </div>
    <span className="relative inline-flex items-center gap-2 text-sm font-semibold mt-6 group-hover:gap-3 transition-[gap]">
      {tr('View the listing', 'सूची देखें')} <ExternalLink size={15} />
    </span>
  </a>
  );
};

export const RadioFeature = ({ idPrefix = 'radio-feature' }) => {
  const [open, setOpen] = useState(false);
  const { tr, lang } = useLang();
  return (
    <div data-testid={idPrefix} className="bg-white border border-teal-50 rounded-3xl p-7 md:p-8 shadow-sm h-full">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-12 h-12 rounded-2xl text-white shrink-0" style={{ background: 'var(--coral)' }}><Radio size={22} /></span>
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase" style={{ color: 'var(--coral)' }}>{RADIO_FEATURE.station} · {RADIO_FEATURE.occasion}</div>
          <h3 className="text-lg md:text-xl text-teal-900" style={{ fontWeight: 600 }}>{tr(RADIO_FEATURE.title, RADIO_FEATURE.hiTitle)}</h3>
        </div>
      </div>
      <p className="text-slate-600 text-sm leading-relaxed mt-4">{tr(RADIO_FEATURE.summary, RADIO_FEATURE.hiSummary)}</p>
      <audio data-testid={`${idPrefix}-player`} controls preload="none" src={RADIO_FEATURE.src} className="w-full mt-5" />
      {lang !== 'hi' && <button
        data-testid={`${idPrefix}-transcript-toggle`}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 mt-4 hover:text-teal-900 transition-colors"
      >
        {open ? 'Hide' : 'Read'} English transcript <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>}
      {open && (
        <ol data-testid={`${idPrefix}-transcript`} className="mt-3 space-y-2 text-sm text-slate-600 border-l-2 border-teal-100 pl-4">
          {RADIO_FEATURE.transcript.map((l) => <li key={l}>{l}</li>)}
        </ol>
      )}
    </div>
  );
};

const Lightbox = ({ src, alt, idPrefix, onClose }) => createPortal(
  <div data-testid={`${idPrefix}-lightbox`} className="fixed inset-0 z-[90] bg-teal-950/85 backdrop-blur-sm overflow-y-auto p-4" onClick={onClose}>
    <button data-testid={`${idPrefix}-close`} onClick={onClose} className="fixed top-4 right-4 grid place-items-center w-11 h-11 rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors"><X size={22} /></button>
    <img src={src} alt={alt} className="mx-auto max-w-3xl w-full rounded-xl shadow-2xl my-10" onClick={(e) => e.stopPropagation()} />
  </div>,
  document.body
);

const PressMeta = ({ item }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase" style={{ color: 'var(--coral)' }}><Newspaper size={16} /> {item.paper} · {item.date}</span>
);

export const PressFeature = ({ item = PRESS_ITEMS[0], idPrefix = 'press-feature' }) => {
  const [full, setFull] = useState(false);
  const { tr } = useLang();
  return (
    <div data-testid={idPrefix} className="grid md:grid-cols-[minmax(0,300px)_1fr] gap-7 md:gap-10 items-start bg-white border border-teal-50 rounded-3xl p-5 md:p-8 shadow-sm">
      <button data-testid={`${idPrefix}-open`} onClick={() => setFull(true)} className="group relative block rounded-2xl overflow-hidden border border-slate-200 shadow-md mx-auto w-full max-w-[300px]">
        <img src={item.clip} alt={item.headline} className="w-full transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-teal-900/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full"><Maximize2 size={13} /> {tr('Full page', 'पूरा पेज')}</span>
      </button>
      <div>
        <PressMeta item={item} />
        <h3 data-testid={`${idPrefix}-title`} className="text-2xl md:text-3xl text-teal-900 mt-3 text-balance" style={{ fontWeight: 600 }}>{tr(item.title, item.hiTitle)}</h3>
        <p className="text-slate-500 text-sm mt-2">{item.headline}</p>
        <ul className="mt-5 space-y-2.5 text-sm text-slate-600">
          {tr(item.summary, item.hiSummary).map((s) => <li key={s} className="flex gap-2"><CheckCircle2 size={17} className="text-teal-600 shrink-0 mt-0.5" />{s}</li>)}
        </ul>
      </div>
      {full && <Lightbox src={item.full} alt={`${item.paper} full page`} idPrefix={idPrefix} onClose={() => setFull(false)} />}
    </div>
  );
};

const PressCard = ({ item }) => {
  const [full, setFull] = useState(false);
  const id = `press-card-${item.id}`;
  return (
    <div data-testid={id} className="card-lift group bg-white border border-teal-50 rounded-3xl overflow-hidden shadow-sm h-full flex flex-col">
      <button data-testid={`${id}-open`} onClick={() => setFull(true)} className="relative block h-80 overflow-hidden bg-slate-50 border-b border-teal-50">
        <img src={item.clip} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-teal-900/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full"><Maximize2 size={13} /> View full</span>
      </button>
      <div className="p-6 flex-1">
        <PressMeta item={item} />
        <h3 className="text-lg text-teal-900 mt-2 text-balance" style={{ fontWeight: 600 }}>{item.title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed mt-2">{item.summary[0]}</p>
      </div>
      {full && <Lightbox src={item.full} alt={item.title} idPrefix={id} onClose={() => setFull(false)} />}
    </div>
  );
};

export const PressGallery = () => (
  <div data-testid="press-gallery" className="grid md:grid-cols-3 sm:grid-cols-2 gap-6">
    {PRESS_ITEMS.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 80}><PressCard item={p} /></Reveal>)}
  </div>
);
